import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const vlFusionComponent: ComponentDef = {
  id: 'vl-fusion',
  name: 'VL Fusion',
  description: 'Fuse vision and language representations for multimodal understanding. Supports cross-attention (Flamingo), MLP projection (LLaVA), and Perceiver resampling.',
  category: 'multimodal',
  params: [
    { name: 'seq_len', label: 'Text length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'n_patches', label: 'Image patches', type: 'number', default: 196, min: 1, max: 1024, step: 1 },
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 768, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 64, step: 1 },
    { name: 'n_latents', label: 'Latent queries (Perceiver)', type: 'number', default: 64, min: 1, max: 512, step: 1 },
    {
      name: 'variant',
      label: 'Fusion variant',
      type: 'select',
      default: 'cross-attention',
      options: [
        { label: 'Cross-Attention (Flamingo)', value: 'cross-attention' },
        { label: 'MLP Projector (LLaVA)', value: 'mlp-projector' },
        { label: 'Perceiver Resampler', value: 'perceiver-resampler' }
      ]
    }
  ],
  buildGraph: (params) => {
    const seq_len = Number(params.seq_len);
    const n_patches = Number(params.n_patches);
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const n_latents = Number(params.n_latents);
    const d_head = d_model / n_heads;
    const variant = String(params.variant || 'cross-attention');

    const g = new GraphBuilder();
    g.input('text', [seq_len, d_model]);
    g.input('image', [n_patches, d_model]);

    if (variant === 'mlp-projector') {
      // ── MLP Projector (LLaVA-style) ──
      // Simple 2-layer MLP projects vision features into text embedding space,
      // then concatenate with text tokens for the LLM to consume.
      g.linear('proj_fc1', 'image', [d_model, d_model * 2]);
      g.activation('proj_gelu', 'proj_fc1', 'GELU');
      g.linear('proj_fc2', 'proj_gelu', [d_model * 2, d_model]);
      g.norm('proj_norm', 'proj_fc2', 'LayerNorm');

      g.concat('fused', ['text', 'proj_norm'], [seq_len + n_patches, d_model]);
      g.linear('out', 'fused', [d_model, d_model]);
      g.output('out', 'out');

    } else if (variant === 'perceiver-resampler') {
      // ── Perceiver Resampler (Flamingo / DeepMind) ──
      // A small set of learned latent queries cross-attends to the full image
      // feature sequence, producing a fixed-size set of resampled vision tokens
      // that are then fused with text via a gating mechanism.

      // Learnable latent queries
      g.embedding('latent_queries', 'image', [n_latents, d_model]);

      // Cross-attention: latent queries (Q) attend to image features (K, V)
      g.linear('resample_q', 'latent_queries', [d_model, d_model]);
      g.linear('resample_k', 'image', [d_model, d_model]);
      g.linear('resample_v', 'image', [d_model, d_model]);

      g.reshape('resample_split_q', 'resample_q', [n_heads, n_latents, d_head]);
      g.reshape('resample_split_k', 'resample_k', [n_heads, n_patches, d_head]);
      g.reshape('resample_split_v', 'resample_v', [n_heads, n_patches, d_head]);

      g.transpose('resample_kt', 'resample_split_k', [n_heads, d_head, n_patches], 'K^T');
      g.matmul('resample_qk', 'resample_split_q', 'resample_kt', [n_heads, n_latents, n_patches]);
      g.scale('resample_scaled', 'resample_qk', '1/sqrt(d_k)');
      g.softmax('resample_attn', 'resample_scaled', -1);
      g.matmul('resample_av', 'resample_attn', 'resample_split_v', [n_heads, n_latents, d_head]);

      g.reshape('resample_out', 'resample_av', [n_latents, d_model]);
      g.linear('resample_wo', 'resample_out', [d_model, d_model]);
      g.norm('resample_norm', 'resample_wo', 'LayerNorm');

      // Second cross-attention pass (resampler block 2) for richer features
      g.linear('resample2_q', 'resample_norm', [d_model, d_model]);
      g.linear('resample2_k', 'image', [d_model, d_model]);
      g.linear('resample2_v', 'image', [d_model, d_model]);

      g.reshape('resample2_split_q', 'resample2_q', [n_heads, n_latents, d_head]);
      g.reshape('resample2_split_k', 'resample2_k', [n_heads, n_patches, d_head]);
      g.reshape('resample2_split_v', 'resample2_v', [n_heads, n_patches, d_head]);

      g.transpose('resample2_kt', 'resample2_split_k', [n_heads, d_head, n_patches], 'K^T');
      g.matmul('resample2_qk', 'resample2_split_q', 'resample2_kt', [n_heads, n_latents, n_patches]);
      g.scale('resample2_scaled', 'resample2_qk', '1/sqrt(d_k)');
      g.softmax('resample2_attn', 'resample2_scaled', -1);
      g.matmul('resample2_av', 'resample2_attn', 'resample2_split_v', [n_heads, n_latents, d_head]);

      g.reshape('resample2_out', 'resample2_av', [n_latents, d_model]);
      g.linear('resample2_wo', 'resample2_out', [d_model, d_model]);
      g.add('resample_residual', 'resample_norm', 'resample2_wo');
      g.norm('vision_tokens', 'resample_residual', 'LayerNorm');

      // Gated fusion: gate controls how much vision info flows into text
      g.linear('text_proj', 'text', [d_model, d_model]);
      g.gate('fusion_gate', 'vision_tokens', [n_latents, d_model]);
      g.fusion('fused', 'text_proj', 'fusion_gate', [seq_len + n_latents, d_model]);
      g.linear('out', 'fused', [d_model, d_model]);
      g.output('out', 'out');

    } else {
      // ── Cross-Attention (Flamingo-style, default) ──
      // Text tokens provide queries; image features provide keys and values.
      // Each text position attends to all image patches via multi-head cross-attention.

      g.linear('q', 'text', [d_model, d_model]);
      g.linear('k', 'image', [d_model, d_model]);
      g.linear('v', 'image', [d_model, d_model]);

      g.reshape('split_q', 'q', [n_heads, seq_len, d_head]);
      g.reshape('split_k', 'k', [n_heads, n_patches, d_head]);
      g.reshape('split_v', 'v', [n_heads, n_patches, d_head]);

      // QK^T: [n_heads, seq_len, d_head] x [n_heads, d_head, n_patches] -> [n_heads, seq_len, n_patches]
      g.transpose('kt', 'split_k', [n_heads, d_head, n_patches], 'K^T');
      g.matmul('qk', 'split_q', 'kt', [n_heads, seq_len, n_patches]);
      g.scale('scaled', 'qk', '1/sqrt(d_k)');
      g.softmax('attn', 'scaled', -1);

      // Weighted sum: [n_heads, seq_len, n_patches] x [n_heads, n_patches, d_head] -> [n_heads, seq_len, d_head]
      g.matmul('attn_v', 'attn', 'split_v', [n_heads, seq_len, d_head]);
      g.reshape('concat_heads', 'attn_v', [seq_len, d_model]);
      g.linear('wo', 'concat_heads', [d_model, d_model]);

      // Gated fusion: vision-conditioned text with residual
      g.gate('vision_gate', 'wo', [seq_len, d_model]);
      g.add('text_residual', 'text', 'vision_gate');
      g.norm('out_norm', 'text_residual', 'LayerNorm');
      g.output('out', 'out_norm');
    }

    return g.build();
  }
};
